import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { random } from 'maath';
import * as THREE from 'three';
import WebGLErrorBoundary from './WebGLErrorBoundary';


const ShootingStar = () => {
    const ref = useRef();
    const [active, setActive] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setActive(true);
            setTimeout(() => setActive(false), 2000); // Animation duration
        }, 15000); // 15 seconds
        return () => clearInterval(interval);
    }, []);

    useFrame((state, delta) => {
        if (active && ref.current) {
            ref.current.position.x += delta * 10;
            ref.current.position.y -= delta * 10;
            ref.current.position.z -= delta * 5;

            if (ref.current.position.x > 10) {
                ref.current.position.set(-10, 10, -5);
            }
        }
    });

    if (!active) return null;

    return (
        <mesh ref={ref} position={[-10, 10, -5]}>
            <sphereGeometry args={[0.05, 16, 16]} />
            <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={2} />
        </mesh>
    );
};

const Stars = (props) => {
    const ref = useRef();
    const sphere = useMemo(() => {
        try {
            return random.inSphere(new Float32Array(6000), { radius: 1.5 });
        } catch (error) {
            console.error("Star generation failed:", error);
            return new Float32Array(0); // Return empty array to avoid crash
        }
    }, []);

    useFrame((state, delta) => {
        if (!ref.current) return;
        // Serene rotation
        ref.current.rotation.x -= delta / 25;
        ref.current.rotation.y -= delta / 30;

        // Smooth Parallax based on scroll
        const scrollY = window.scrollY;
        const targetY = -(scrollY * 0.0003);
        ref.current.position.y = THREE.MathUtils.lerp(ref.current.position.y, targetY, 0.1);

        // Interactive part: Gentle drift
        const { x, y } = state.mouse;
        ref.current.position.x = THREE.MathUtils.lerp(ref.current.position.x, x * 0.1, 0.05);
    });

    return (
        <group rotation={[0, 0, Math.PI / 4]}>
            <Points ref={ref} positions={sphere} stride={3} frustumCulled {...props}>
                <PointMaterial
                    transparent
                    color="#ffffff"
                    size={0.008}
                    sizeAttenuation={true}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </Points>
        </group>
    );
};

const Background3D = () => {
    return (
        <div className="fixed inset-0 z-[-1] pointer-events-none bg-[#020205]">
            <WebGLErrorBoundary>
                <Canvas
                    camera={{ position: [0, 0, 1] }}
                    dpr={[1, 1.5]}
                    gl={{
                        antialias: false,
                        powerPreference: "default",
                        preserveDrawingBuffer: true
                    }}
                >
                    <Stars />
                    <ShootingStar />
                    <ambientLight intensity={0.4} color="#111133" />
                </Canvas>
            </WebGLErrorBoundary>
        </div>
    );
};

export default Background3D;

